import React, { memo, useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import { TableTag } from './TableTag'
import { Tooltip, Typography } from '@mui/material'
import { addS } from 'src/utils/baseUtils'
import { colors } from 'src/theme'

const APPROX_TAG_WIDTH = 150

interface Props {
  tags: string[]
  cutLength?: number
  tooltipThreshold?: number
}

const TableTagsComponent = ({ tags, cutLength = 10, tooltipThreshold = 10 }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const [maxVisible, setMaxVisible] = useState(tags.length)

  const visibleTags = tags.slice(0, maxVisible)
  const hiddenTags = tags.slice(maxVisible)
  const hiddenCount = hiddenTags.length

  useEffect(() => {
    const container = containerRef.current

    if (!container) {
      return
    }

    const updateVisibleCount = () => {
      const containerWidth = container.offsetWidth
      const availableWidth = containerWidth - 10
      const visibleCount = Math.floor(availableWidth / APPROX_TAG_WIDTH)

      setMaxVisible(Math.max(visibleCount, 1))
    }

    updateVisibleCount()
    window.addEventListener('resize', updateVisibleCount)

    return () => window.removeEventListener('resize', updateVisibleCount)
  }, [tags])

  return (
    <div
      ref={containerRef}
      css={tw`flex items-center gap-2 overflow-hidden whitespace-nowrap w-full max-w-full`}
    >
      {visibleTags.map((item) => (
        <div key={item} css={tw`flex-shrink-0`}>
          <TableTag tag={item} cutLength={cutLength} tooltipThreshold={tooltipThreshold} />
        </div>
      ))}
      {hiddenCount > 0 && (
        <Tooltip title={hiddenTags.join(', ')} placement="bottom-start">
          <div css={tw`flex-shrink-0 rounded py-0.5 px-2 bg-[#D8E5FF]`}>
            <Typography variant="subtitle2" color={colors.COLORS_BLUE_500}>
              {`+${hiddenCount} ${addS(hiddenCount, 'tag')}`}
            </Typography>
          </div>
        </Tooltip>
      )}
    </div>
  )
}

export const TableTags = memo(TableTagsComponent)
